import { type FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';

import { RefreshedPollResultsBars } from '@soomo/lib/components/pageElements/PollQuestion/RefreshedPoll/index';
import RefreshedPollResultsPie from '@soomo/lib/components/pageElements/PollQuestion/RefreshedPoll/ResultsPie/index';
import { uvPollColors } from '@soomo/lib/components/pageElements/PollQuestion/utils';

import { type RootState } from '@editor2/reducers/root';
import { type Poll, type PollChoice } from '@editor2/types/pageElements';
import { pollChartPreview } from './styles';

interface Props {
	element: Poll;
	courseId: number;
}

const PollResultsChartPreview: FC<Props> = (props) => {
	const { element, courseId } = props;

	const [viewSampleData, setViewSampleData] = useState(false);

	const { course, config } = useSelector((state: RootState) => state.courses[courseId]);
	const { color_palettes: configColorPalettes } = config;

	const colorPalette = configColorPalettes?.[element.color_palette] || uvPollColors;

	const classData = useMemo(() => {
		return viewSampleData
			? getSampleClassData(element.choices)
			: getDefaultClassData(element.choices);
	}, [element.choices, viewSampleData]);

	return (
		<div className={pollChartPreview}>
			<Form.Check
				id="sampleDataToggle"
				type="switch"
				checked={viewSampleData}
				label="View sample class data"
				className="sampleDataToggle"
				onChange={() => setViewSampleData(!viewSampleData)}
			/>
			{element.chart_type === 'bar' && (
				<RefreshedPollResultsBars
					questionFamilyId={element.family_id}
					courseNumber={course.number}
					choices={element.choices}
					classData={classData}
					externalData={element.data}
					totalCount={element.total_count}
					dataType={element.data_type}
					dataGroupBy={element.data_group_by}
					choiceOrdering={element.choice_ordering}
					colorPalette={colorPalette}
					colorGroupBy={element.color_group_by}
				/>
			)}
			{element.chart_type === 'pie' && (
				<RefreshedPollResultsPie
					questionFamilyId={element.family_id}
					classData={classData}
					colorPalette={colorPalette}
					choices={element.choices}
					choiceOrdering={element.choice_ordering}
					isHighContrast={false}
				/>
			)}
		</div>
	);
};

export default PollResultsChartPreview;

const getSampleClassData = (choices: PollChoice[]) => {
	return choices.map((choice) => ({ label: choice.body, data: Math.random() * 100 }));
};

const getDefaultClassData = (choices: PollChoice[]) => {
	return choices.map((choice) => ({ label: choice.body, data: 0 }));
};
